/**
 * Shop inventory filter helpers (TNK-105 phase 2). Pure functions that turn
 * the operator-side StorefrontCard projection into the richer six-tier
 * "visual rarity" + foil signal the Figma "Revamp Card Shop Display" design
 * uses.
 *
 * Why a derived rarity: our canonical CardRarity vocabulary is
 * Common | Uncommon | Rare | Mythic | Trainer | Energy | Promo. The "Ultra
 * Rare / Secret Rare" feel lives on the `finish` axis (Holo / Premium /
 * Reverse Holo) per packages/normalize/src/rarity.ts. Promoting those into
 * visual tiers gives the design its tier-color treatment without changing
 * the data model.
 */

import type { StorefrontCard } from "./storefront";

export type ShopRarity =
  | "Common"
  | "Uncommon"
  | "Rare"
  | "Ultra Rare"
  | "Secret Rare"
  | "Legendary";

const RARITY_ORDER: Record<ShopRarity, number> = {
  Legendary: 0,
  "Secret Rare": 1,
  "Ultra Rare": 2,
  Rare: 3,
  Uncommon: 4,
  Common: 5,
};

/**
 * Maps (rarity, finish) → six-tier visual rarity. Mythic always promotes to
 * Legendary. Premium finishes (gold / rainbow / hyper / illustration / alt
 * art) read as Secret Rare. Holo / Reverse Holo on a base Rare reads as
 * Ultra Rare. Trainer / Energy / Promo fall back to Rare for the chrome
 * since they're niche groupings, not pull-tier indicators.
 */
export function visualRarity(card: StorefrontCard): ShopRarity {
  const rarity = card.rarity;
  const finish = card.finish;
  if (rarity === "Mythic") return "Legendary";
  if (rarity === "Common") return "Common";
  if (rarity === "Uncommon") return "Uncommon";
  if (finish === "Premium") return "Secret Rare";
  if (finish === "Holo" || finish === "Reverse Holo") return "Ultra Rare";
  return "Rare";
}

/** Anything with a print treatment counts as foil for the design's indicator. */
export function isFoil(finish: string | null | undefined): boolean {
  return (
    finish !== null &&
    finish !== undefined &&
    finish !== "" &&
    finish !== "Normal" &&
    finish !== "Non-Foil"
  );
}

/** Sorted, de-duped strings present in `field` across all cards (skips empties). */
function uniqueValues<K extends keyof StorefrontCard>(
  cards: StorefrontCard[],
  field: K,
): string[] {
  const set = new Set<string>();
  for (const c of cards) {
    const v = c[field];
    if (typeof v === "string" && v.length > 0) set.add(v);
  }
  return Array.from(set).sort();
}

/** Sorted, de-duped values present in a string-array field across all
 *  cards (e.g. `tcgTypes`). Mirrors `uniqueValues` for jsonb arrays. */
function uniqueArrayValues<K extends keyof StorefrontCard>(
  cards: StorefrontCard[],
  field: K,
): string[] {
  const set = new Set<string>();
  for (const c of cards) {
    const v = c[field];
    if (!Array.isArray(v)) continue;
    for (const item of v) {
      if (typeof item === "string" && item.length > 0) set.add(item);
    }
  }
  return Array.from(set).sort();
}

export interface FilterOptions {
  games: string[];
  rarities: ShopRarity[];
  /** Card "class" — tcgSupertype: Pokemon/Trainer/Energy for Pokemon;
   *  Creature/Sorcery/etc for Magic. Cross-game union. New code
   *  prefers `pokemonEnergies` + `magicCardTypes` for the buyer's
   *  per-game filter sections. */
  types: string[];
  /** Element / card-type axis (legacy union). Kept for back-compat. */
  elements: string[];
  /** Pokémon-only energies (Fire, Water, Psychic, …). Server returns
   *  this as the intersection of `tcg_types` with the canonical
   *  POKEMON_ENERGIES vocabulary. */
  pokemonEnergies: string[];
  /** Magic-only primary card types (Creature, Sorcery, Land, …).
   *  Server returns this as the intersection of `tcg_supertype` with
   *  the canonical MAGIC_CARD_TYPES vocabulary. */
  magicCardTypes: string[];
  /** Magic color identity — mtgColors entries (W/U/B/R/G) plus the
   *  synthetic `C` (Colorless) when at least one eligible Magic card
   *  has an explicitly-empty mtgColors array. TNK-121 + storefront
   *  follow-up. */
  colors: string[];
  conditions: string[];
  priceCap: number;
}

/**
 * Derive the filter pickers' available options from the actual inventory so
 * we never offer a value that would filter the grid to zero (e.g., a Pokemon-
 * only store seeing "Creature/Spell/Trap" type options is noise).
 */
export function extractFilterOptions(cards: StorefrontCard[]): FilterOptions {
  const rarities = new Set<ShopRarity>();
  for (const c of cards) rarities.add(visualRarity(c));
  const ordered = Array.from(rarities).sort(
    (a, b) => RARITY_ORDER[a] - RARITY_ORDER[b],
  );
  // Round the price cap up to the next sensible bucket so the Max input
  // defaults wide enough for the priciest card.
  const maxPrice = cards.reduce(
    (m, c) => Math.max(m, c.expectedSalePrice || c.marketPrice || 0),
    0,
  );
  const priceCap = Math.ceil(maxPrice / 100) * 100 || 1000;
  // Color sort follows the canonical WUBRG order so the filter UI
  // doesn't read as alphabetical noise (B, G, R, U, W) — Magic players
  // read color identity in print order.
  const WUBRG_ORDER: Record<string, number> = {
    W: 0,
    U: 1,
    B: 2,
    R: 3,
    G: 4,
  };
  const colors = uniqueArrayValues(cards, "mtgColors").sort(
    (a, b) =>
      (WUBRG_ORDER[a] ?? 99) - (WUBRG_ORDER[b] ?? 99) || a.localeCompare(b),
  );
  // Best-effort client derivation of the per-game axes (legacy code
  // path — server now returns these in /api/store/facets). Filter
  // the cross-game tcgSupertype / tcgTypes columns down to the
  // canonical vocabularies so they don't bleed cross-game.
  const POKEMON_ENERGIES_C = new Set([
    "Colorless",
    "Darkness",
    "Dragon",
    "Fairy",
    "Fighting",
    "Fire",
    "Grass",
    "Lightning",
    "Metal",
    "Psychic",
    "Water",
  ]);
  const MAGIC_CARD_TYPES_C = new Set([
    "Artifact",
    "Creature",
    "Enchantment",
    "Instant",
    "Land",
    "Planeswalker",
    "Sorcery",
    "Tribal",
  ]);
  const allTypes = uniqueValues(cards, "tcgSupertype");
  const allElements = uniqueArrayValues(cards, "tcgTypes");
  return {
    games: uniqueValues(cards, "game"),
    rarities: ordered,
    types: allTypes,
    elements: allElements,
    pokemonEnergies: allElements.filter((v) => POKEMON_ENERGIES_C.has(v)),
    magicCardTypes: allTypes.filter((v) => MAGIC_CARD_TYPES_C.has(v)),
    colors,
    conditions: uniqueValues(cards, "condition"),
    priceCap,
  };
}

export type SortBy =
  | "newest"
  | "price-asc"
  | "price-desc"
  | "name-asc"
  | "rarity";

export interface ShopFilterState {
  search: string;
  game: string[];
  rarity: ShopRarity[];
  type: string[];
  /** TNK-120 follow-up — match a card when at least one of its `tcgTypes`
   *  is in the selected set. Pokemon: elemental types (Fire/Water).
   *  Magic: card types (Creature/Sorcery/Land). */
  element: string[];
  /** TNK-121 — Magic color identity (W/U/B/R/G). Match-any semantics:
   *  selecting U+R matches blue, red, and Izzet cards. Colorless cards
   *  (`mtgColors === []`) match only when the user explicitly picks
   *  the Colorless pill, which appears as `"C"` in the option list. */
  color: string[];
  condition: string[];
  priceMin: number;
  priceMax: number;
  foilOnly: boolean;
  sortBy: SortBy;
}

export function defaultFilterState(priceCap: number): ShopFilterState {
  return {
    search: "",
    game: [],
    rarity: [],
    type: [],
    element: [],
    color: [],
    condition: [],
    priceMin: 0,
    priceMax: priceCap,
    foilOnly: false,
    sortBy: "newest",
  };
}

/** Count of non-default filter axes — drives the "Filters (N)" badge. */
export function activeFilterCount(
  filters: ShopFilterState,
  priceCap: number,
): number {
  return (
    filters.game.length +
    filters.rarity.length +
    filters.type.length +
    filters.element.length +
    filters.color.length +
    filters.condition.length +
    (filters.foilOnly ? 1 : 0) +
    (filters.priceMin > 0 || filters.priceMax < priceCap ? 1 : 0)
  );
}

import { storefrontPrice } from "./storefront";

/** Apply the filter state + sort to the inventory list. */
export function applyShopFilters(
  cards: StorefrontCard[],
  filters: ShopFilterState,
): StorefrontCard[] {
  const q = filters.search.trim().toLowerCase();
  let out = cards.filter((c) => {
    if (
      q &&
      !c.name.toLowerCase().includes(q) &&
      !c.setName.toLowerCase().includes(q) &&
      !c.setCode.toLowerCase().includes(q) &&
      !c.cardNumber.toLowerCase().includes(q)
    ) {
      return false;
    }
    if (filters.game.length && !filters.game.includes(c.game)) return false;
    if (filters.rarity.length && !filters.rarity.includes(visualRarity(c)))
      return false;
    if (
      filters.type.length &&
      (c.tcgSupertype === null || !filters.type.includes(c.tcgSupertype))
    )
      return false;
    if (
      filters.element.length &&
      (c.tcgTypes === null ||
        !c.tcgTypes.some((t) => filters.element.includes(t)))
    )
      return false;
    if (filters.color.length) {
      // Empty-array colors === colorless; map to the synthetic "C" pill so
      // the operator can filter for it explicitly.
      if (c.mtgColors === null) return false;
      const effective = c.mtgColors.length === 0 ? ["C"] : c.mtgColors;
      if (!effective.some((c2) => filters.color.includes(c2))) return false;
    }
    if (filters.condition.length && !filters.condition.includes(c.condition))
      return false;
    if (filters.foilOnly && !isFoil(c.finish)) return false;
    const price = storefrontPrice(c);
    if (price < filters.priceMin || price > filters.priceMax) return false;
    return true;
  });

  switch (filters.sortBy) {
    case "price-asc":
      out = [...out].sort((a, b) => storefrontPrice(a) - storefrontPrice(b));
      break;
    case "price-desc":
      out = [...out].sort((a, b) => storefrontPrice(b) - storefrontPrice(a));
      break;
    case "name-asc":
      out = [...out].sort((a, b) => a.name.localeCompare(b.name));
      break;
    case "rarity":
      out = [...out].sort(
        (a, b) => RARITY_ORDER[visualRarity(a)] - RARITY_ORDER[visualRarity(b)],
      );
      break;
    case "newest":
      // Newest is a no-op here — the server already returns rows in insertion
      // order (no createdAt in projection). Reverse so latest entries lead.
      out = [...out].reverse();
      break;
  }
  return out;
}
